import TagManager from 'react-gtm-module';

import {
  DeferredNavigationProps,
  deferredNavigationFields,
} from './clickTracking';

/** GA4 `item_list_name` for every add-to-cart originating from an Image
    Hotspot tile (ED-447) — the banner isn't a PLP, so it gets one constant
    value rather than the page URL Quick Add otherwise captures. */
export const HOTSPOT_ITEM_LIST_NAME = 'Hotspot';

export interface TrackHotspotTabClickProps {
  data: {
    /** Path the click happened on, e.g. `/en/category/camping-gear` */
    interactionLocation: string;
    /** The newly-selected tab's label. */
    linkText: string;
    coOpBrand: string | null;
    /** 0-based position of the tab within the bar. */
    index: number;
  };
}

/**
 * ED-416 (1) — fired whenever the active tab changes, from either the
 * desktop pill row or the mobile dropdown (`ImageTabBar`'s single
 * `onTabChange`), not just an explicit click — arrow-key/Home/End selection
 * goes through the same handler, and this reports the newly active tab, not
 * a "clicked" element that may not exist for keyboard selection.
 */
export const trackHotspotTabClick = ({ data }: TrackHotspotTabClickProps) => {
  const { interactionLocation, linkText, coOpBrand, index } = data;

  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'click_hotspot_tab',
      interaction: {
        interaction_location: interactionLocation,
        link_text: linkText,
        coop_brand: coOpBrand,
        index,
      },
    },
  });
};

export interface TrackHotspotViewProps {
  data: {
    interactionLocation: string;
    /** The hotspot's product title. */
    linkText: string;
    /** The hotspot's product PDP URL. */
    linkUrl: string;
    coOpBrand: string | null;
    /** 0-based position of the hotspot within its tab (top-to-bottom, then
        left-to-right — matches `ImageHotspot`'s own render order). */
    index: number;
  };
}

/**
 * ED-416 (2) — fired once per hotspot the first time its dot scrolls into
 * view (an impression, not a click) — see `HotspotPopover`'s
 * `useObjectVisibility` usage.
 */
export const trackHotspotView = ({ data }: TrackHotspotViewProps) => {
  const { interactionLocation, linkText, linkUrl, coOpBrand, index } = data;

  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'hotspot_view',
      interaction: {
        interaction_location: interactionLocation,
        link_text: linkText,
        link_url: linkUrl,
        coop_brand: coOpBrand,
        index,
      },
    },
  });
};

export interface TrackHotspotClickProps {
  data: {
    interactionLocation: string;
    linkUrl: string;
    linkText: string;
    /** The active tab's label at the moment the dot was clicked. */
    tabName: string;
    coOpBrand: string | null;
    index: number;
  };
}

/**
 * ED-416 (3) — fired when a hotspot dot is clicked to open its product tile
 * (not when the tile's own image/name link is later followed to the PDP —
 * that's untracked, matching the ticket's event list).
 */
export const trackHotspotClick = ({ data }: TrackHotspotClickProps) => {
  const { interactionLocation, linkUrl, linkText, tabName, coOpBrand, index } =
    data;

  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'click_hotspot',
      interaction: {
        interaction_location: interactionLocation,
        link_url: linkUrl,
        link_text: linkText,
        tab_name: tabName,
        coop_brand: coOpBrand,
        index,
      },
    },
  });
};

export interface TrackHotspotCtaClickProps extends DeferredNavigationProps {
  data: {
    interactionLocation: string;
    /** Resolved href of the CTA link. */
    linkUrl: string;
    linkText: string;
    tabName: string;
    coOpBrand: string | null;
    /** 0-based position of the CTA within its tab (up to 2 per tab). */
    index: number;
  };
}

/**
 * ED-416 (4) — a tab's CTA link (`ReviewPanel`'s `CtaLinks`). A real `<a
 * href>` navigation, so — like `trackPlpCrossLinkingCardClick` — supports
 * `eventCallback`/`eventTimeout` to defer navigation until the hit is sent.
 */
export const trackHotspotCtaClick = ({
  data,
  ...deferral
}: TrackHotspotCtaClickProps) => {
  const { interactionLocation, linkUrl, linkText, tabName, coOpBrand, index } =
    data;

  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'click_hotspot_cta',
      interaction: {
        interaction_location: interactionLocation,
        link_url: linkUrl,
        link_text: linkText,
        tab_name: tabName,
        coop_brand: coOpBrand,
        index,
      },
      ...deferredNavigationFields(deferral),
    },
  });
};

export interface TrackHotspotQuickAddClickProps {
  data: {
    interactionLocation: string;
    linkUrl: string;
    linkText: string;
    tabName: string;
    coOpBrand: string | null;
    index: number;
  };
}

/**
 * ED-447 (1) — fired when a hotspot tile's Quick Add trigger opens the
 * size-selector popover, mirroring `utils/gtm/ga4/trackQuickAddClick`'s own
 * `click_quick_atc` (fired the same way — trigger-open, not a completed add)
 * but with the richer `tab_name`/`coop_brand`/`index` fields this ticket
 * calls for, which the generic PLP-grid event doesn't carry. `QuickAdd`
 * fires this itself via `ProductTile`'s `onTriggerOpen` override instead of
 * its own default `trackQuickAddClick` call.
 */
export const trackHotspotQuickAddClick = ({
  data,
}: TrackHotspotQuickAddClickProps) => {
  const { interactionLocation, linkUrl, linkText, tabName, coOpBrand, index } =
    data;

  TagManager.dataLayer({
    dataLayer: {
      event: 'user_interaction',
      event_name: 'click_hotspot_quick_atc',
      interaction: {
        interaction_location: interactionLocation,
        link_url: linkUrl,
        link_text: linkText,
        tab_name: tabName,
        coop_brand: coOpBrand,
        index,
      },
    },
  });
};
